import pb from "./pocketbase";

const VISITOR_KEY = "uud_visitor_id";
const COLLECTION = "analytics_events";

var lastPagePath = "";
var lastPageTime = 0;

/** Cached browser-side geo (~public egress); used when PocketBase only sees a private IP. */
var clientGeoCache = null;
var clientGeoCacheAt = 0;
var CLIENT_GEO_TTL_MS = 25 * 60 * 1000;

function uuidV4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    var r = (Math.random() * 16) | 0;
    var v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getOrCreateVisitorId() {
  try {
    var existing = localStorage.getItem(VISITOR_KEY);
    if (existing && String(existing).length >= 8) return String(existing).trim();
    var id = uuidV4();
    localStorage.setItem(VISITOR_KEY, id);
    return id;
  } catch (_) {
    return uuidV4();
  }
}

function safeUa() {
  try {
    var ua = typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "";
    return ua.length > 400 ? ua.slice(0, 400) : ua;
  } catch (_) {
    return "";
  }
}

/**
 * Best-effort country/city from the visitor network (browser → public geo API).
 * @returns {Promise<{ country: string, city: string } | null>}
 */
async function fetchClientGeoHint() {
  if (typeof fetch === "undefined") return null;
  var now = Date.now();
  if (clientGeoCache && now - clientGeoCacheAt < CLIENT_GEO_TTL_MS) {
    return clientGeoCache;
  }
  var timeoutMs = 4000;
  var result = null;

  var fetchJson = async function (url) {
    var ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    var tid = ctrl ? setTimeout(function () { ctrl.abort(); }, timeoutMs) : null;
    try {
      var res = await fetch(url, { signal: ctrl ? ctrl.signal : undefined });
      if (tid) clearTimeout(tid);
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      if (tid) clearTimeout(tid);
      return null;
    }
  };

  var j1 = await fetchJson("https://ipwho.is/");
  if (j1 && j1.success === true) {
    result = {
      country: String(j1.country || "").slice(0, 120),
      city: String(j1.city || "").slice(0, 120),
    };
  }
  if (!result || (!result.country && !result.city)) {
    var j2 = await fetchJson("https://ipapi.co/json/");
    if (j2 && !j2.error && !j2.reason) {
      result = {
        country: String(j2.country_name || j2.country || "").slice(0, 120),
        city: String(j2.city || "").slice(0, 120),
      };
    }
  }
  if (result && (result.country || result.city)) {
    clientGeoCache = result;
    clientGeoCacheAt = now;
    return result;
  }
  return null;
}

export async function trackPageView(pathname) {
  if (process.env.REACT_APP_ANALYTICS === "0") return;
  var path = String(pathname || "").trim();
  if (!path || path.length > 512) return;
  var now = Date.now();
  if (path === lastPagePath && now - lastPageTime < 1500) return;
  lastPagePath = path;
  lastPageTime = now;
  var visitorId = getOrCreateVisitorId();
  try {
    var geo = await fetchClientGeoHint();
    var body = {
      eventType: "page_view",
      visitorId,
      path,
      userAgent: safeUa(),
    };
    if (geo) {
      if (geo.country) body.country = geo.country;
      if (geo.city) body.city = geo.city;
    }
    await pb.collection(COLLECTION).create(body);
  } catch (e) {
    console.warn("analytics page_view", e);
  }
}

/**
 * @param {{ itemId: string, category: string, clickTarget?: string, path?: string }} opts
 */
export async function trackCardClick(opts) {
  if (process.env.REACT_APP_ANALYTICS === "0") return;
  var itemId = String(opts?.itemId || "").trim();
  var category = String(opts?.category || "").trim().toLowerCase();
  var allowed = ["tools", "resos", "ideaz", "apps"];
  if (!itemId || allowed.indexOf(category) < 0) return;
  var clickTarget = String(opts?.clickTarget || "").trim().slice(0, 32);
  var path =
    String(opts?.path || (typeof window !== "undefined" ? window.location.pathname : "") || "")
      .trim()
      .slice(0, 512);
  var visitorId = getOrCreateVisitorId();
  try {
    var geo = await fetchClientGeoHint();
    var body = {
      eventType: "card_click",
      visitorId,
      path,
      userAgent: safeUa(),
      itemId,
      category,
      clickTarget: clickTarget || "",
    };
    if (geo) {
      if (geo.country) body.country = geo.country;
      if (geo.city) body.city = geo.city;
    }
    await pb.collection(COLLECTION).create(body);
  } catch (e) {
    console.warn("analytics card_click", e);
  }
}

/**
 * Log which modal/popup was opened (Redux-driven UI).
 * @param {string} popupId e.g. login, signup, contact, validation
 */
export async function trackPopupOpen(popupId) {
  if (process.env.REACT_APP_ANALYTICS === "0") return;
  var id = String(popupId || "").trim().slice(0, 80);
  if (!id) return;
  var path = "";
  if (typeof window !== "undefined" && window.location && window.location.pathname) {
    path = String(window.location.pathname).trim().slice(0, 512);
  }
  if (!path) path = "/";
  var visitorId = getOrCreateVisitorId();
  try {
    var geo = await fetchClientGeoHint();
    var body = {
      eventType: "popup_open",
      visitorId,
      path,
      userAgent: safeUa(),
      popupId: id,
    };
    if (geo) {
      if (geo.country) body.country = geo.country;
      if (geo.city) body.city = geo.city;
    }
    await pb.collection(COLLECTION).create(body);
  } catch (e) {
    console.warn("analytics popup_open", e);
  }
}
